import type { ReactElement } from 'react';
import type { FieldProps } from '@rjsf/utils';

import type { ImageLinkItem } from './ImageLinkRow';
import type { ImageLinkManagerWidgetProps } from './ImageLinkManagerWidget';
import { ImageLinkManagerWidget } from './ImageLinkManagerWidget';

// ErrorNode and helpers mirror menuItemErrors.ts. childNode guards against
// __errors arrays being mistaken for child nodes.
interface ErrorNode {
  __errors?: string[];
  [field: string]: ErrorNode | string[] | undefined;
}

function childNode(node: ErrorNode | undefined, key: string): ErrorNode | undefined {
  const child = node?.[key];
  return Array.isArray(child) ? undefined : (child as ErrorNode | undefined);
}

function hasDeepErrors(node: ErrorNode | undefined): boolean {
  if (!node) return false;
  if (node.__errors?.length) return true;
  return Object.keys(node).some(
    (key) => key !== '__errors' && hasDeepErrors(childNode(node, key))
  );
}

// Cross-reference against the current value so that a stale errorSchema entry
// (from a previous submit where the URL was invalid) is suppressed once the
// user has corrected it — RJSF only refreshes errorSchema on the next submit.
// new URL() is a reliable proxy for the uri format used by AJV/ajv-formats.
function isValidUri(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function getItemErrors(
  items: ImageLinkItem[],
  errorSchema: unknown
): Record<number, string | undefined> {
  const result: Record<number, string | undefined> = {};
  if (!errorSchema || typeof errorSchema !== 'object') return result;

  const root = errorSchema as Record<string, ErrorNode>;
  items.forEach((item, i) => {
    const node = root[String(i)];
    // Check for ANY deep error on the item node rather than a specific AJV path.
    // AJV with if/then or oneOf schemas can place errors at either the property
    // level (node.linkHref.__errors) or the object level (node.__errors).
    // hasDeepErrors catches both. The message is then derived from the item's
    // actual data, not the raw AJV text — same technique as menuItemErrors.ts.
    if (!hasDeepErrors(node)) return;

    if (!item.linkHref?.trim()) {
      result[i] = 'Link is required';
    } else if (item.isReadOnly === false && !isValidUri(item.linkHref)) {
      // External link whose current value still fails URI format. Suppresses
      // stale errorSchema errors once the user types a valid URL.
      result[i] = 'Enter a full URL, e.g. https://example.com';
    }
  });
  return result;
}

// Implemented as a field rather than a widget: RJSF only passes `errorSchema`
// (per-item validation errors) to fields, never to custom array widgets.
// ArrayField intercepts `ui:widget: imageLinkManager` and routes here so that
// per-item link errors reach each row inline.
export function ImageLinkManagerField(props: FieldProps<ImageLinkItem[]>): ReactElement {
  const { formData, onChange, uiSchema, schema, errorSchema, fieldPathId } = props;
  const items = formData ?? [];
  const itemErrors = getItemErrors(items, errorSchema);

  const widgetProps = {
    value: items,
    onChange: (next: unknown) => onChange(next as ImageLinkItem[], fieldPathId.path),
    uiSchema: uiSchema ?? {},
    schema,
  } as unknown as ImageLinkManagerWidgetProps;

  return <ImageLinkManagerWidget {...widgetProps} itemErrors={itemErrors} />;
}
